import { useEffect, useState, type ReactNode } from 'react';
import { SectionSurface } from '../../components/role-ui';
import {
  getCajas,
  getCajaActual,
  getAperturaSugeridaCaja,
  abrirCaja,
  cerrarCaja,
  agregarViajeCaja,
  quitarViajeCaja,
  actualizarMovimientoCaja,
  descargarExcelCaja,
  getViajesDisponiblesCaja,
  PROVINCIA_POR_DEFECTO,
  type ActualizacionMovimientoCaja,
  type AperturaSugeridaCaja,
  type ViajeDisponibleCaja,
} from '../../lib/caja';
import type { Caja, MovimientoCaja } from '../../lib/appTypes';
import { Modal, ModalCloseButton, avisar, confirmar, mensajeDeError, SkeletonRows } from '../../components/ui-kit';

function PanelPopup({
  children,
  onClose,
}: {
  children: ReactNode;
  onClose: () => void;
}) {
  return (
    <Modal onClose={onClose} maxWidthClassName="max-w-[98vw] 2xl:max-w-[1800px]" panelClassName="max-h-[96vh]">
      <div className="px-4 pb-6 pt-14 sm:px-6">{children}</div>
    </Modal>
  );
}

/** Popup compacto para abrir una caja nueva con su valor inicial. */
function AperturaCajaPopup({
  sugerida,
  cargando,
  error,
  valor,
  onValorChange,
  guardando,
  onCancelar,
  onConfirmar,
}: {
  sugerida: AperturaSugeridaCaja | null;
  cargando: boolean;
  error: string;
  valor: string;
  onValorChange: (valor: string) => void;
  guardando: boolean;
  onCancelar: () => void;
  onConfirmar: () => void;
}) {
  const numero = Number(valor);
  const valorValido = valor.trim() !== '' && Number.isFinite(numero) && numero > 0;
  const ajustado = sugerida !== null && valorValido && Math.abs(numero - sugerida.valor_sugerido) >= 0.005;

  return (
    <Modal onClose={onCancelar} maxWidthClassName="max-w-md" dismissible={!guardando} showCloseButton={!guardando} ariaLabel="Abrir nueva caja">
      <div className="p-6">
        <h3 className="pr-10 text-xl font-semibold text-stone-950">Abrir nueva caja</h3>
        <p className="mt-1 text-sm text-stone-500">
          Revisa el valor inicial. Una vez abierta la caja, este valor no se puede modificar.
        </p>

        {cargando ? (
          <div className="mt-5 rounded-[var(--radius-panel)] border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700">
            Calculando sobrante de la caja anterior...
          </div>
        ) : (
          <>
            {sugerida && (
              <dl className="mt-5 space-y-2 rounded-[var(--radius-panel)] border border-stone-200 bg-stone-50 p-4 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-stone-500">Valor base de caja</dt>
                  <dd className="font-semibold text-stone-800">{formatCurrency(sugerida.valor_base)}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-stone-500">
                    Sobrante {sugerida.caja_anterior_id ? `de la caja #${sugerida.caja_anterior_id}` : 'de la caja anterior'}
                  </dt>
                  <dd className="font-semibold text-emerald-700">+ {formatCurrency(sugerida.sobrante_anterior)}</dd>
                </div>
                <div className="flex justify-between gap-4 border-t border-stone-200 pt-2">
                  <dt className="font-semibold text-stone-700">Total sugerido</dt>
                  <dd className="font-bold text-stone-950">{formatCurrency(sugerida.valor_sugerido)}</dd>
                </div>
              </dl>
            )}

            <label className="mt-5 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">
              Valor de la nueva caja
            </label>
            <div className="mt-2 flex items-center rounded-[14px] border border-stone-300 bg-white px-3 focus-within:border-stone-400 focus-within:ring-4 focus-within:ring-stone-100">
              <span className="mr-1 font-medium text-stone-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0.01"
                autoFocus
                value={valor}
                onChange={e => onValorChange(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && valorValido && !guardando) onConfirmar(); }}
                className="w-full bg-transparent py-2.5 text-lg font-semibold text-stone-900 outline-none"
                placeholder="0.00"
              />
            </div>
            {!valorValido && (
              <p className="mt-2 text-xs font-medium text-rose-600">El valor debe ser mayor que $0.00.</p>
            )}
            {ajustado && sugerida && (
              <p className="mt-2 text-xs text-amber-700">
                Valor ajustado manualmente (sugerido: {formatCurrency(sugerida.valor_sugerido)}).
              </p>
            )}
          </>
        )}

        {error && (
          <div className="mt-4 rounded-[14px] border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <ModalCloseButton
            disabled={guardando}
            className="ui-btn ui-btn-secondary"
          >
            Cancelar
          </ModalCloseButton>
          <button
            type="button"
            onClick={onConfirmar}
            disabled={cargando || guardando || !valorValido}
            className="ui-btn ui-btn-primary"
          >
            {guardando ? 'Abriendo...' : 'Abrir caja'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** Texto "Base $200,00 + sobrante caja #3 $15,00" para cajas abiertas con este registro. */
function detalleApertura(caja: Caja): string {
  if (caja.valor_base === null) return '';
  let texto = `Base ${formatCurrency(caja.valor_base)}`;
  if (caja.sobrante_anterior > 0) {
    texto += ` + sobrante${caja.caja_anterior ? ` caja #${caja.caja_anterior}` : ''} ${formatCurrency(caja.sobrante_anterior)}`;
  }
  return texto;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('es-EC', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
}

function formatDate(dateString: string | number | null | undefined): string {
  if (!dateString) return '—';
  const parsed = new Date(dateString);
  if (Number.isNaN(parsed.getTime())) return String(dateString);
  return new Intl.DateTimeFormat('es-EC', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);
}

// Interfaz para la tabla editable
interface EditableRow {
  id: number;
  fechaCreacion: string;
  numeroFactura: string;
  nombreCliente: string;
  telefonoCliente: string;
  vendedor: string;
  provinciaDestino: string;
  destino: string;
  nombreSucursal: string;
  valorEntregadoLogistico: number;
  valorRealPagado: number;
  companiaEnvio: string;
  companiaTransporte: string;
  nombreLogistico: string;
  logistico: string;
  cambio: number;
  [key: string]: string | number; // Para poder iterar y actualizar
}

const readOnlyCellCls = 'px-3 py-2 text-stone-700 whitespace-nowrap';
const editableInputCls = 'w-full rounded border border-stone-200 bg-white px-2 py-1.5 outline-none focus:border-stone-400 focus:ring-1 focus:ring-stone-300';

export default function AdminCajaPanel() {
  const [cajas, setCajas] = useState<Caja[]>([]);
  const [loading, setLoading] = useState(false);
  const [dataError, setDataError] = useState('');

  const [showPopup, setShowPopup] = useState(false);
  const [cajaActiva, setCajaActiva] = useState<Caja | null>(null);
  const [cajaSeleccionada, setCajaSeleccionada] = useState<Caja | null>(null);

  // Para apertura (popup con valor base + sobrante de la caja anterior)
  const [showAperturaPopup, setShowAperturaPopup] = useState(false);
  const [aperturaSugerida, setAperturaSugerida] = useState<AperturaSugeridaCaja | null>(null);
  const [cargandoApertura, setCargandoApertura] = useState(false);
  const [valorApertura, setValorApertura] = useState('');
  const [errorApertura, setErrorApertura] = useState('');

  // Para el popup de editar (agregar viajes)
  const [viajesEnvio, setViajesEnvio] = useState<ViajeDisponibleCaja[]>([]);
  const [selectedViajeId, setSelectedViajeId] = useState<string>('');
  const [loadingAction, setLoadingAction] = useState(false);
  const [savingField, setSavingField] = useState<number | null>(null);
  const [exportingCajaId, setExportingCajaId] = useState<number | null>(null);

  // Estado local para la tabla editable
  const [filasEditables, setFilasEditables] = useState<EditableRow[]>([]);

  // Paginación Cajas
  const [cajaPage, setCajaPage] = useState(1);
  const [cajaTotalPages, setCajaTotalPages] = useState(1);
  const [cajaTotalRegistros, setCajaTotalRegistros] = useState(0);

  // Paginación Viajes
  const [viajePage, setViajePage] = useState(1);
  const [viajeTotalPages, setViajeTotalPages] = useState(1);
  const [viajeSearch, setViajeSearch] = useState('');

  useEffect(() => {
    void loadCajas(1);
    void loadCajaActiva();
    void loadViajes(1);
  }, []);

  async function loadCajaActiva() {
    try {
      const openCaja = await getCajaActual();
      if (openCaja && openCaja.estado === 'abierta') {
        setCajaActiva(openCaja);
      }
    } catch {
      setCajaActiva(null);
    }
  }

  function handleAbrirPopup(caja: Caja) {
    setCajaSeleccionada(caja);
    mapCajaToEditableRows(caja.movimientos);
    setShowPopup(true);
  }

  function handleGuardarCambios() {
    // Simplemente cierra el popup; los datos ya se persisten al editar cada campo
    setShowPopup(false);
    setCajaSeleccionada(null);
  }

  async function loadCajas(page: number) {
    try {
      setLoading(true);
      const cajasData = await getCajas(page, 10);
      setCajas(cajasData.items);
      setCajaPage(cajasData.page);
      setCajaTotalPages(cajasData.totalPages);
      setCajaTotalRegistros(cajasData.count);

      setDataError('');
    } catch (err: unknown) {
      setDataError((err as { message?: string })?.message ?? 'Error al cargar las cajas.');
    } finally {
      setLoading(false);
    }
  }

  function mapCajaToEditableRows(movimientos: MovimientoCaja[] | undefined) {
    const rows: EditableRow[] = (movimientos || []).map(mov => {
      const valorLogistico = mov.valor_logistico ?? mov.valor_total_envio ?? 0;
      return {
        id: mov.id,
        fechaCreacion: formatDate(mov.fecha_creacion),
        numeroFactura: mov.numero_factura || '',
        nombreCliente: mov.nombre_cliente || '',
        telefonoCliente: mov.telefono_cliente || '',
        vendedor: mov.vendedor_usuario || '',
        provinciaDestino: mov.provincia_destino ?? PROVINCIA_POR_DEFECTO,
        destino: mov.destino || '',
        nombreSucursal: mov.sucursal_entrega || '',
        valorEntregadoLogistico: valorLogistico,
        valorRealPagado: mov.valor_real_pagado || 0,
        companiaEnvio: mov.compania_transporte_envio || '',
        companiaTransporte: mov.compania_transporte || '',
        nombreLogistico: mov.nombre_logistico || '',
        logistico: mov.conductor_asignado || '',
        cambio: valorLogistico - (mov.valor_real_pagado || 0),
      };
    });
    setFilasEditables(rows);
  }

  async function loadViajes(page: number, search: string = viajeSearch) {
    try {
      const params: { page: number; page_size: number; search?: string } = { page, page_size: 10 };
      if (search) {
        params.search = search;
      }
      const response = await getViajesDisponiblesCaja(params);
      setViajesEnvio(response?.items || []);
      setViajePage(response?.page || 1);
      setViajeTotalPages(response?.totalPages || 1);
    } catch (error) {
      console.error('Error loading viajes', error);
    }
  }

  useEffect(() => {
    if (showPopup) {
      void loadViajes(1);
    }
  }, [showPopup]);

  function applyCajaActualizada(cajaActualizada: Caja) {
    if (cajaSeleccionada && cajaSeleccionada.id === cajaActualizada.id) {
      setCajaSeleccionada(cajaActualizada);
      mapCajaToEditableRows(cajaActualizada.movimientos);
    }
    if (cajaActiva && cajaActiva.id === cajaActualizada.id) {
      setCajaActiva(cajaActualizada);
    }
    setCajas(prev => prev.map(c => c.id === cajaActualizada.id ? cajaActualizada : c));
  }

  const handleMostrarApertura = async () => {
    setShowAperturaPopup(true);
    setAperturaSugerida(null);
    setValorApertura('');
    setErrorApertura('');
    try {
      setCargandoApertura(true);
      const sugerida = await getAperturaSugeridaCaja();
      setAperturaSugerida(sugerida);
      setValorApertura(sugerida.valor_sugerido.toFixed(2));
    } catch (err: unknown) {
      setErrorApertura((err as { message?: string })?.message ?? 'No se pudo calcular el valor sugerido. Ingresa el valor manualmente.');
    } finally {
      setCargandoApertura(false);
    }
  };

  const handleAbrirCaja = async () => {
    const valor = Number(valorApertura);
    if (!Number.isFinite(valor) || valor <= 0) {
      setErrorApertura('El valor de la caja debe ser mayor que $0.00.');
      return;
    }
    try {
      setLoadingAction(true);
      setErrorApertura('');
      await abrirCaja(Math.round(valor * 100) / 100);
      setShowAperturaPopup(false);
      await loadCajaActiva();
      await loadCajas(1);
    } catch (err: unknown) {
      setErrorApertura((err as { message?: string })?.message ?? 'Error al abrir la caja.');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleCerrarCaja = async () => {
    if (!(await confirmar({ titulo: '¿Cerrar esta caja definitivamente?', mensaje: 'Ya no podrás agregar más envíos a esta caja.', textoAceptar: 'Cerrar caja', tono: 'advertencia' }))) return;
    try {
      setLoadingAction(true);
      await cerrarCaja();
      setShowPopup(false);
      setCajaSeleccionada(null);
      await loadCajaActiva();
      await loadCajas(1);
    } catch (error) {
      avisar('Error al cerrar la caja.', 'error');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleAgregarViaje = async () => {
    if (!selectedViajeId) return;
    try {
      setLoadingAction(true);
      const cajaActualizada = await agregarViajeCaja(selectedViajeId);
      applyCajaActualizada(cajaActualizada);
      await loadCajas(cajaPage);
      await loadViajes(viajePage);
      setSelectedViajeId('');
    } catch (err: unknown) {
      avisar(mensajeDeError(err, 'Error al agregar viaje a la caja. Puede que ya esté registrado.'), 'error');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleFieldChange = (id: number, field: keyof EditableRow, value: string) => {
    setFilasEditables(prev => prev.map(row => {
      if (row.id === id) {
        const parsedVal = (field === 'valorEntregadoLogistico' || field === 'valorRealPagado' || field === 'cambio') ? Number(value) : value;
        const newRow = { ...row, [field]: parsedVal };
        // Recalcular el cambio si es necesario
        if (field === 'valorEntregadoLogistico' || field === 'valorRealPagado') {
          newRow.cambio = Number(newRow.valorEntregadoLogistico) - Number(newRow.valorRealPagado);
        }
        return newRow;
      }
      return row;
    }));
  };

  /** Guarda en el backend los campos editables de una fila. */
  const guardarMovimiento = async (row: EditableRow, cambios: ActualizacionMovimientoCaja) => {
    try {
      setSavingField(row.id);
      const cajaActualizada = await actualizarMovimientoCaja(row.id, cambios);
      applyCajaActualizada(cajaActualizada);
    } catch (err: unknown) {
      avisar(mensajeDeError(err, 'No se pudo guardar el cambio en la caja.'), 'error');
    } finally {
      setSavingField(null);
    }
  };

  const handleBlurValorReal = (row: EditableRow) => {
    const val = Number(row.valorRealPagado);
    if (Number.isNaN(val)) return;
    void guardarMovimiento(row, { valor_real_pagado: val });
  };

  const handleBlurValorLogistico = (row: EditableRow) => {
    const val = Number(row.valorEntregadoLogistico);
    if (Number.isNaN(val) || val < 0) return;
    void guardarMovimiento(row, { valor_logistico: val });
  };

  const handleBlurCompaniaTransporte = (row: EditableRow) => {
    void guardarMovimiento(row, { compania_transporte: String(row.companiaTransporte ?? '').trim() });
  };

  const handleBlurProvincia = (row: EditableRow) => {
    void guardarMovimiento(row, { provincia_destino: String(row.provinciaDestino ?? '').trim() });
  };

  const handleQuitarViaje = async (movimientoId: number) => {
    if (!(await confirmar({ titulo: '¿Quitar esta entrega de la caja?', mensaje: 'La entrega volverá a la lista de envíos disponibles.', textoAceptar: 'Quitar' }))) return;
    try {
      setLoadingAction(true);
      const cajaActualizada = await quitarViajeCaja(movimientoId);
      applyCajaActualizada(cajaActualizada);
      await loadViajes(viajePage);
    } catch (err: unknown) {
      avisar(mensajeDeError(err, 'Error al quitar la entrega de la caja.'), 'error');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleExportXLSX = async (caja: Caja) => {
    if (!caja.movimientos || caja.movimientos.length === 0) {
      avisar('La caja no tiene movimientos para exportar.', 'advertencia');
      return;
    }
    try {
      setExportingCajaId(caja.id);
      await descargarExcelCaja(caja.id);
    } catch (err: unknown) {
      avisar(mensajeDeError(err, 'No se pudo exportar la caja.'), 'error');
    } finally {
      setExportingCajaId(null);
    }
  };

  const cajasAbiertas = cajas.filter(c => c.estado === 'abierta').length;

  return (
    <>
      <section className="space-y-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-end">
          {!cajaActiva && (
            <button
              type="button"
              onClick={() => void handleMostrarApertura()}
              disabled={loadingAction}
              className="ui-btn ui-btn-primary"
            >
              Abrir caja
            </button>
          )}
        </div>

        {dataError && (
          <div className="rounded-[var(--radius-panel)] border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700 shadow-sm">
            {dataError}
          </div>
        )}

        <div className="grid gap-4 xl:grid-cols-2">
          <article className="ui-card p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">Total cajas registradas</p>
            <p className="mt-4 text-3xl font-semibold text-stone-950">{cajaTotalRegistros}</p>
          </article>
          <article className="ui-card p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">Cajas abiertas</p>
            <p className="mt-4 text-3xl font-semibold text-stone-950">{cajasAbiertas}</p>
          </article>
        </div>

        <SectionSurface className="space-y-5">
          <h4 className="text-lg font-semibold text-stone-900">Historial de cajas</h4>

          {loading ? (
            <SkeletonRows etiqueta="Cargando cajas..." />
          ) : cajas.length === 0 ? (
            <div className="rounded-[var(--radius-card)] border border-dashed border-stone-300 bg-white/70 p-10 text-center text-stone-500">
              No hay cajas registradas.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="ui-tabla min-w-full border-separate border-spacing-y-2 text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-[0.18em] text-[var(--freisa-text-muted)]">
                    <th className="pb-2 pr-4 font-semibold">ID</th>
                    <th className="pb-2 pr-4 font-semibold">Estado</th>
                    <th className="pb-2 pr-4 font-semibold">Valor Apertura</th>
                    <th className="pb-2 pr-4 font-semibold">Fecha Apertura</th>
                    <th className="pb-2 pr-4 font-semibold">Fecha Cierre</th>
                    <th className="pb-2 pr-4 font-semibold">Recaudado</th>
                    <th className="pb-2 font-semibold text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {cajas.map(caja => (
                    <tr key={caja.id} className="bg-[var(--freisa-bg)]/70 text-[var(--freisa-text)] align-top">
                      <td className="rounded-l-[16px] px-4 py-3 font-semibold">
                        #{caja.id}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full border px-3 py-1 text-[10px] uppercase font-bold tracking-wider ${caja.estado === 'abierta' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-stone-50 text-stone-700 border-stone-200'}`}>
                          {caja.estado}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-semibold text-[var(--freisa-text-soft)]">
                        {formatCurrency(caja.valor_total_apertura)}
                        {detalleApertura(caja) && (
                          <span className="block text-[11px] font-normal text-stone-400">{detalleApertura(caja)}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[var(--freisa-text-soft)]">
                        {formatDate(caja.fecha_apertura)}
                      </td>
                      <td className="px-4 py-3 text-[var(--freisa-text-soft)]">
                        {formatDate(caja.fecha_cierre)}
                      </td>
                      <td className="px-4 py-3 font-semibold text-[var(--freisa-text-soft)]">
                        {formatCurrency(caja.total_recaudado)}
                      </td>
                      <td className="rounded-r-[16px] px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => handleAbrirPopup(caja)}
                            className="ui-btn ui-btn-secondary ui-btn-sm"
                          >
                            Editar Caja
                          </button>
                          <button
                            type="button"
                            onClick={() => void handleExportXLSX(caja)}
                            disabled={exportingCajaId === caja.id}
                            className="ui-btn ui-btn-info ui-btn-sm"
                          >
                            {exportingCajaId === caja.id ? 'Exportando...' : 'Exportar XLSX'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex items-center justify-between border-t border-stone-200 bg-stone-50 px-4 py-3">
                <span className="text-sm text-stone-500">Página {cajaPage} de {cajaTotalPages}</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => loadCajas(cajaPage - 1)}
                    disabled={cajaPage <= 1 || loading}
                    className="rounded-xl border border-stone-200 bg-white px-3 py-1.5 text-sm font-semibold text-stone-700 transition hover:bg-stone-50 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => loadCajas(cajaPage + 1)}
                    disabled={cajaPage >= cajaTotalPages || loading}
                    className="ui-btn ui-btn-secondary ui-btn-sm"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </div>
          )}
        </SectionSurface>
      </section>

      {showPopup && cajaSeleccionada && (
        <PanelPopup onClose={() => { setShowPopup(false); setCajaSeleccionada(null); }}>
          <div className="flex justify-between items-start mb-6">
            <div>
              <h3 className="text-xl font-semibold text-stone-950 mb-1">Caja #{cajaSeleccionada.id} - Movimientos</h3>
              <p className="text-sm text-stone-500">
                Valor de apertura: <span className="font-semibold text-stone-800">{formatCurrency(cajaSeleccionada.valor_total_apertura)}</span>
                {detalleApertura(cajaSeleccionada) && (
                  <span className="ml-1 text-stone-400">({detalleApertura(cajaSeleccionada)})</span>
                )}
                <span className={`ml-3 rounded-full border px-3 py-1 text-[10px] uppercase font-bold tracking-wider ${cajaSeleccionada.estado === 'abierta' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-stone-50 text-stone-700 border-stone-200'}`}>
                  {cajaSeleccionada.estado}
                </span>
              </p>
            </div>
          </div>

          <div className="mb-6 flex flex-col items-start gap-4 rounded-[var(--radius-panel)] border border-stone-200 bg-stone-50 p-4 xl:flex-row xl:items-end">
            <div className="w-full flex-1">
              <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Buscar por factura, fecha o cliente</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Ej: 001-002-123 o 2026-05-26 o Juan Perez"
                  value={viajeSearch}
                  onChange={e => setViajeSearch(e.target.value)}
                  className="ui-input flex-1"
                />
                <button
                  type="button"
                  onClick={() => loadViajes(1, viajeSearch)}
                  className="ui-btn ui-btn-dark"
                >
                  Buscar
                </button>
              </div>
            </div>

            <div className="w-full flex-[2]">
              <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Seleccionar Envío (vendedores MATRIZ)</label>
              <div className="flex gap-2">
                <select
                  className="ui-input min-w-0 flex-1"
                  value={selectedViajeId}
                  onChange={e => setSelectedViajeId(e.target.value)}
                >
                  <option value="">-- Selecciona una entrega --</option>
                  {viajesEnvio.map(v => (
                    <option key={v.id} value={v.id}>
                      {v.numero_factura ? `Fact: ${v.numero_factura} - ` : ''}Fecha: {formatDate(v.created_at)} - Sucursal: {v.sucursal_nombre || 'N/A'} - Vendedor: {v.vendedor_usuario || 'N/A'} - Total: {formatCurrency(v.valor_total_envio || 0)}
                    </option>
                  ))}
                </select>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => loadViajes(viajePage - 1)}
                    disabled={viajePage <= 1}
                    className="flex h-[42px] w-[42px] items-center justify-center rounded-[14px] border border-stone-300 bg-white text-stone-600 transition hover:bg-stone-50 disabled:opacity-50"
                  >
                    ←
                  </button>
                  <button
                    type="button"
                    onClick={() => loadViajes(viajePage + 1)}
                    disabled={viajePage >= viajeTotalPages}
                    className="flex h-[42px] w-[42px] items-center justify-center rounded-[14px] border border-stone-300 bg-white text-stone-600 transition hover:bg-stone-50 disabled:opacity-50"
                  >
                    →
                  </button>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => void handleAgregarViaje()}
              disabled={!selectedViajeId || loadingAction}
              className="ui-btn ui-btn-primary whitespace-nowrap"
            >
              Agregar a caja
            </button>
          </div>

          <div className="mb-8">
            <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
              <h4 className="text-sm font-semibold uppercase tracking-wider text-stone-500">Tabla Editable de Entregas</h4>
              <p className="text-xs text-stone-500">Los campos con fondo blanco se guardan automáticamente al salir del campo.</p>
            </div>
            {filasEditables.length === 0 ? (
              <div className="rounded-[16px] border border-dashed border-stone-300 bg-white p-6 text-center text-sm text-stone-500">
                No hay movimientos registrados en esta caja.
              </div>
            ) : (
              <div className="rounded-[16px] border border-stone-200 overflow-x-auto custom-scrollbar">
                <table className="ui-tabla min-w-max text-xs text-left">
                  <thead className="bg-stone-100 text-stone-500 uppercase tracking-wider">
                    <tr>
                      <th className="px-3 py-3 font-semibold border-b border-stone-200 min-w-[140px]">Fecha Creación</th>
                      <th className="px-3 py-3 font-semibold border-b border-stone-200 min-w-[130px]">N° Factura</th>
                      <th className="px-3 py-3 font-semibold border-b border-stone-200 min-w-[160px]">Nombre Cliente</th>
                      <th className="px-3 py-3 font-semibold border-b border-stone-200 min-w-[110px]">Teléfono</th>
                      <th className="px-3 py-3 font-semibold border-b border-stone-200 min-w-[120px]">Vendedor</th>
                      <th className="px-3 py-3 font-semibold border-b border-stone-200 min-w-[200px]">Destino (provincia)</th>
                      <th className="px-3 py-3 font-semibold border-b border-stone-200 min-w-[120px]">Sucursal</th>
                      <th className="px-3 py-3 font-semibold border-b border-stone-200 min-w-[120px]">V. Logístico</th>
                      <th className="px-3 py-3 font-semibold border-b border-stone-200 min-w-[120px]">V. Real Pagado</th>
                      <th className="px-3 py-3 font-semibold border-b border-stone-200 min-w-[150px]">Compañía (envío)</th>
                      <th className="px-3 py-3 font-semibold border-b border-stone-200 min-w-[180px]">Compañía Transporte Real</th>
                      <th className="px-3 py-3 font-semibold border-b border-stone-200 min-w-[140px]">Logístico</th>
                      <th className="px-3 py-3 font-semibold border-b border-stone-200 min-w-[100px]">Cambio</th>
                      <th className="px-3 py-3 font-semibold border-b border-stone-200 min-w-[80px]">Acción</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100 bg-white">
                    {filasEditables.map((row) => (
                      <tr key={row.id} className={`transition-colors hover:bg-stone-50 ${savingField === row.id ? 'opacity-60' : ''}`}>
                        <td className={readOnlyCellCls}>{row.fechaCreacion}</td>
                        <td className={readOnlyCellCls}>{row.numeroFactura || '—'}</td>
                        <td className={readOnlyCellCls}>{row.nombreCliente || '—'}</td>
                        <td className={readOnlyCellCls}>{row.telefonoCliente || '—'}</td>
                        <td className={`${readOnlyCellCls} font-semibold`}>{row.vendedor || '—'}</td>
                        <td className="p-1 border-r border-stone-100">
                          <input
                            type="text"
                            value={row.provinciaDestino}
                            placeholder="Ej: Pichincha"
                            maxLength={120}
                            onChange={e => handleFieldChange(row.id, 'provinciaDestino', e.target.value)}
                            onBlur={() => handleBlurProvincia(row)}
                            className={editableInputCls}
                          />
                          {row.destino && (
                            <p className="mt-1 max-w-[220px] truncate px-1 text-[10px] text-stone-400" title={row.destino}>
                              {row.destino}
                            </p>
                          )}
                        </td>
                        <td className={readOnlyCellCls}>{row.nombreSucursal || '—'}</td>
                        <td className="p-1 border-r border-stone-100">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={row.valorEntregadoLogistico}
                            onChange={e => handleFieldChange(row.id, 'valorEntregadoLogistico', e.target.value)}
                            onBlur={() => handleBlurValorLogistico(row)}
                            className={editableInputCls}
                          />
                        </td>
                        <td className="p-1 border-r border-stone-100">
                          <input
                            type="number"
                            step="0.01"
                            value={row.valorRealPagado}
                            onChange={e => handleFieldChange(row.id, 'valorRealPagado', e.target.value)}
                            onBlur={() => handleBlurValorReal(row)}
                            className={`${editableInputCls} font-semibold`}
                          />
                        </td>
                        <td className={readOnlyCellCls}>{row.companiaEnvio || '—'}</td>
                        <td className="p-1 border-r border-stone-100">
                          <input
                            type="text"
                            value={row.companiaTransporte}
                            placeholder="Ej: SERVIENTREGA"
                            onChange={e => handleFieldChange(row.id, 'companiaTransporte', e.target.value)}
                            onBlur={() => handleBlurCompaniaTransporte(row)}
                            className={editableInputCls}
                          />
                        </td>
                        <td className={readOnlyCellCls}>{row.nombreLogistico || '—'}</td>
                        <td className="p-2 font-bold text-[var(--freisa-red)]">
                          {formatCurrency(row.cambio)}
                        </td>
                        <td className="p-2">
                          <button
                            type="button"
                            onClick={() => void handleQuitarViaje(row.id)}
                            disabled={loadingAction || savingField === row.id}
                            className="rounded-lg border border-rose-200 bg-rose-50 px-2 py-1 text-xs font-semibold text-rose-700 transition hover:bg-rose-100 disabled:opacity-50"
                          >
                            Quitar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-4 border-t border-stone-200 pt-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-col gap-4 sm:flex-row sm:gap-8">
              <div className="flex flex-col">
                <span className="text-xs uppercase tracking-wider font-semibold text-stone-500">Recaudado total (V. Real Pagado)</span>
                <span className="text-2xl font-bold text-stone-950">{formatCurrency(filasEditables.reduce((acc, row) => acc + Number(row.valorRealPagado || 0), 0))}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-xs uppercase tracking-wider font-semibold text-stone-500">Valor Restante (Caja - Real Pagado)</span>
                <span className="text-2xl font-bold text-[var(--freisa-red)]">
                  {formatCurrency(cajaSeleccionada.valor_total_apertura - filasEditables.reduce((acc, row) => acc + Number(row.valorRealPagado || 0), 0))}
                </span>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => void handleExportXLSX(cajaSeleccionada)}
                disabled={exportingCajaId === cajaSeleccionada.id || filasEditables.length === 0}
                className="ui-btn ui-btn-info ui-btn-lg"
              >
                {exportingCajaId === cajaSeleccionada.id ? 'Exportando...' : 'Exportar XLSX'}
              </button>
              <button
                type="button"
                onClick={() => handleGuardarCambios()}
                disabled={loadingAction}
                className="ui-btn ui-btn-primary ui-btn-lg"
              >
                Guardar Cambios
              </button>
              {cajaSeleccionada.estado === 'abierta' && (
                <button
                  type="button"
                  onClick={() => void handleCerrarCaja()}
                  disabled={loadingAction}
                  className="ui-btn ui-btn-dark ui-btn-lg"
                >
                  Cerrar Caja Definitivamente
                </button>
              )}
            </div>
          </div>
        </PanelPopup>
      )}

      {showAperturaPopup && (
        <AperturaCajaPopup
          sugerida={aperturaSugerida}
          cargando={cargandoApertura}
          error={errorApertura}
          valor={valorApertura}
          onValorChange={valor => { setValorApertura(valor); setErrorApertura(''); }}
          guardando={loadingAction}
          onCancelar={() => setShowAperturaPopup(false)}
          onConfirmar={() => void handleAbrirCaja()}
        />
      )}
    </>
  );
}
